import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import swal from "sweetalert";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseIcon from "@mui/icons-material/Close";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import PageContainer from "../../../components/container/PageContainer";
import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";
import {
  ApproveOrRejectEmployee,
  GetEmployeeById,
  GetPendingEmployeeApprovals,
  GetAllDepartments,
} from "../../../services/admin";
import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";
import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";
import { routes } from "../../../routes/routes";

const BCrumb = [
  { to: "/", title: "HR Management" },
  { title: "Employee Approval" },
];

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const EmployeeApprovalPage = () => {
  const theme = useTheme();
  const { personId } = useParams();
  const navigate = useNavigate();

  // State for data
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Filter state
  const [globalSearch, setGlobalSearch] = useState("");
  const [searchText, setSearchText] = useState({});
  const [showAllFilters, setShowAllFilters] = useState(false);

  // Sort state
  const [orderDirection, setOrderDirection] = useState("asc");
  const [valueToOrderBy, setValueToOrderBy] = useState("employeeId");

  // Other state
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load data once on page load
  const loadAllData = async () => {
    setLoading(true);
    try {
      const response = await GetPendingEmployeeApprovals({
        start: 0,
        length: 10000, // Get all records
        searchKeyword: null,
      });

      if (response?.status === 200) {
        const records = response?.data?.result || response?.data?.data || [];
        setAllData(Array.isArray(records) ? records : []);
      } else {
        toast.error("Unable to load pending employee approvals");
      }
    } catch (error) {
      toast.error("Unable to load pending employee approvals");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadAllData();
  }, []);

  const formatDateForSearch = (value) => {
    if (!value) return "";

    // Take only the date part
    const datePart = value.toString().split(" ")[0];

    // API format: dd-MM-yyyy (e.g., "11-11-2022")
    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      // Check if it's dd-MM-yyyy format (day first)
      if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        const [dd, mm, yyyy] = parts;
        return `${yyyy}-${mm}-${dd}`;
      }
      // If it's yyyy-MM-dd format, return as is
      if (parts[0].length === 4) {
        return datePart;
      }
    }

    // API format: MM/dd/yyyy
    if (datePart.includes("/")) {
      const [mm, dd, yyyy] = datePart.split("/");
      return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
    }

    return datePart;
  };

  // Apply filters function
  const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) return [];

    let filtered = [...data];

    // Global search - search across all fields
    if (globalSearch?.trim()) {
      const searchValue = globalSearch.trim().toLowerCase();
      filtered = filtered.filter((row) => {
        // Search in all relevant fields
        const searchableFields = [
          row?.employeeId,
          row?.personId,
          row?.employeeID,
          row?.employeeName,
          row?.firstName,
          row?.middleName,
          row?.lastName,
          row?.department,
          row?.deptName,
          row?.designation,
          row?.empPosition,
          row?.role,
          row?.reportingUnder,
          row?.reportingTo,
          row?.deputyManager,
          row?.managerName,
        ];

        return searchableFields.some(field =>
          field?.toString().toLowerCase().includes(searchValue)
        );
      });
    }

    // Column filters - only if there are any
    const hasColumnFilters = Object.values(searchText).some(v => v?.trim());

    if (hasColumnFilters) {
      const fieldMap = {
        employeeId: ["employeeId", "personId", "employeeID"],
        employeeName: ["employeeName", "firstName", "lastName"],
        department: ["department", "deptName"],
        role: ["role"],
        reportingUnder: ["reportingUnder", "reportingTo"],
        deputyManager: ["deputyManager", "managerName"],
        createdDate: ["createdDate", "createdOn"],
        doj: ["doj", "dateOfJoining"],
        designation: ["designation", "empPosition"],
      };

      filtered = filtered.filter((row) => {
        return Object.keys(searchText).every((key) => {
          const filterValue = searchText[key]?.trim();
          if (!filterValue) return true;

          // For date fields, compare the formatted date
          if (key === "createdDate" || key === "doj") {
            const fields = fieldMap[key] || [key];
            return fields.some((field) => {
              const rowValue = row[field];
              if (!rowValue) return false;

              // Format the row date to yyyy-MM-dd for comparison
              const displayDate = formatDate(rowValue); // dd-MM-yyyy

              const [dd, mm, yyyy] = displayDate.split("-");
              const searchDate = `${yyyy}-${mm}-${dd}`;

              return searchDate === filterValue;
            });
          }

          // For text fields, do case-insensitive search
          const searchValue = filterValue.toLowerCase();
          const fields = fieldMap[key] || [key];

          return fields.some((field) => {
            const value = row[field]?.toString().toLowerCase() || "";
            return value.includes(searchValue);
          });
        });
      });
    }

    return filtered;
  }, [globalSearch, searchText]);

  // Apply filters whenever data or filters change
  useEffect(() => {
    if (allData.length > 0) {
      const result = applyFilters(allData);
      setFilteredData(result);
      setPage(0);
    }
  }, [allData, globalSearch, searchText, applyFilters]);

  // Sort data client-side
  const getSortedData = useCallback((data) => {
    if (!data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aVal = a[valueToOrderBy]?.toString().toLowerCase() || "";
      let bVal = b[valueToOrderBy]?.toString().toLowerCase() || "";

      // Handle null/undefined values
      if (!aVal && !bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;

      if (orderDirection === "asc") {
        return aVal.localeCompare(bVal);
      }
      return bVal.localeCompare(aVal);
    });
  }, [valueToOrderBy, orderDirection]);

  // Memoized sorted and paginated data
  const paginatedData = useMemo(() => {
    const sorted = getSortedData(filteredData);
    if (rowsPerPage === -1) return sorted;
    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, getSortedData, page, rowsPerPage]);

  // Load by ID from URL
  useEffect(() => {
    if (!personId) return;

    const loadById = async () => {
      try {
        const response = await GetEmployeeById(personId);
        if (response?.status === 200) {
          setSelectedRecord(response?.data?.result || response?.data || null);
          setDetailsOpen(true);
        }
      } catch (error) {
        toast.error("Unable to load employee details from the link");
      }
    };

    loadById();
  }, [personId]);

  // View employee - navigate to employee view page
  const handleViewEmployee = (employeeId) => {
    if (employeeId) {
      navigate(routes?.employeeView?.build?.(`${employeeId}`) || `/admin/employee-view/${employeeId}`);
    }
  };

  const handleApprove = async () => {
    if (!selectedRecord) return;

    swal({
      title: "Confirm Approval",
      text: "Are you sure you want to approve this employee?",
      icon: "warning",
      buttons: ["Cancel", "Approve"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (confirmed) {
        setSubmitting(true);
        try {
          const response = await ApproveOrRejectEmployee({
            personId: selectedRecord?.personId || selectedRecord?.employeeId,
            approvalStatus: 1,
            modifiedMessage: ""
          });
          if (response?.status === 200 || response?.status === 201) {
            toast.success("Employee approved successfully");
            setApproveOpen(false);
            setDetailsOpen(false);
            // Refresh data
            loadAllData();
            swal({
              title: "Success",
              text: "Employee approved successfully",
              icon: "success",
              timer: 2000,
              buttons: false,
            });
          } else {
            toast.error(response?.data?.message || "Approval failed");
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || "Approval failed");
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleReject = async () => {
    if (!selectedRecord || !rejectReason.trim()) {
      toast.error("Please enter a rejection reason");
      return;
    }

    swal({
      title: "Confirm Rejection",
      text: "Are you sure you want to reject this employee?",
      icon: "warning",
      buttons: ["Cancel", "Reject"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (confirmed) {
        setSubmitting(true);
        try {
          const response = await ApproveOrRejectEmployee({
            personId: selectedRecord?.personId || selectedRecord?.employeeId,
            approvalStatus: 2,
            modifiedMessage: rejectReason.trim()
          });
          if (response?.status === 200 || response?.status === 201) {
            toast.success("Employee rejected successfully");
            setRejectOpen(false);
            setRejectReason("");
            setDetailsOpen(false);
            // Refresh data
            loadAllData();
            swal({
              title: "Success",
              text: "Employee rejected successfully",
              icon: "success",
              timer: 2000,
              buttons: false,
            });
          } else {
            toast.error(response?.data?.message || "Rejection failed");
          }
        } catch (error) {
          toast.error(error?.response?.data?.message || "Rejection failed");
        } finally {
          setSubmitting(false);
        }
      }
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlRequestSort = (property) => {
    const isAscending = valueToOrderBy === property && orderDirection === "asc";
    setValueToOrderBy(property);
    setOrderDirection(isAscending ? "desc" : "asc");
  };

  const handleReset = () => {
    setGlobalSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  const clearColumnFilter = (field) => {
    setSearchText((prev) => {
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
    setPage(0);
  };

  const resetColumnFilters = () => {
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  const isColumnFiltered = (field) =>
    searchText[field] !== null &&
    searchText[field] !== undefined &&
    String(searchText[field]).trim() !== "";

  const columnFilterCount = Object.values(searchText || {}).filter(
    (v) => v !== null && v !== undefined && String(v).trim() !== ""
  ).length;

  const hasColumnFilters = columnFilterCount > 0;

  const renderDetails = (record) => {
    if (!record) return null;

    const detailFields = [
      { label: "Employee ID", value: record?.employeeId || record?.personId || record?.employeeID },
      { label: "Employee Name", value: record?.employeeName || [record?.firstName, record?.middleName, record?.lastName].filter(Boolean).join(" ") },
      { label: "Department", value: record?.department || record?.deptName },
      { label: "Designation", value: record?.designation || record?.empPosition },
      { label: "Role", value: record?.role },
      { label: "DOJ", value: record?.doj || record?.dateOfJoining },
      { label: "Reporting Under", value: record?.reportingUnder || record?.reportingTo },
      { label: "Deputy Manager", value: record?.deputyManager || record?.managerName },
      { label: "Created Date", value: record?.createdDate || record?.createdOn },
    ];

    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
        {detailFields.map((item) => (
          <Box
            key={item.label}
            sx={{
              p: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
              {item.label}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.5 }}>
              {item.value || "-"}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const datePart = value.toString().split(" ")[0];

    // dd-MM-yyyy
    if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) {
      return datePart;
    }

    // MM/dd/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(datePart)) {
      const [mm, dd, yyyy] = datePart.split("/");
      return `${dd}-${mm}-${yyyy}`;
    }

    const d = new Date(value);

    if (!isNaN(d.getTime())) {
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${d.getFullYear()}`;
    }

    return "-";
  };

  return (
    <div>
      <Breadcrumb title="Employee Approval" items={BCrumb} />
      <PageContainer title="Employee Approval" description="Pending employee approvals">
        <Card sx={{ p: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Box display="flex" alignItems="center">
              <FeatherIcon icon="user-check" width="18" />
              <Box sx={{ ml: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Pending Employee Approvals ({filteredData.length})
                </Typography>
                {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Box>
            </Box>
          </Box>

          <Card>
            <Box padding={1}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  mb: 1,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  "& > .MuiGrid-item": {
                    flexShrink: 0,
                  },
                }}
              >
                <Grid item sx={{ width: 280 }}>
                  <CustomTextField
                    name="globalSearch"
                    value={globalSearch}
                    fullWidth
                    size="small"
                    placeholder="Search across all fields..."
                    onChange={(e) => {
                      setGlobalSearch(e.target.value);
                      setPage(0);
                      resetColumnFilters();
                    }}
                    InputProps={{
                      endAdornment: globalSearch && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setGlobalSearch("");
                              setPage(0);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item sx={{ flex: "0 0 auto" }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FilterAltIcon
                      sx={{
                        cursor: "pointer",
                        color: showAllFilters ? "primary.main" : "inherit",
                      }}
                      onClick={() => setShowAllFilters(!showAllFilters)}
                    />

                    {hasColumnFilters && !showAllFilters && (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => setShowAllFilters(true)}
                      >
                        Column Filters ({columnFilterCount})
                        <CloseIcon
                          sx={{ fontSize: 16 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            resetColumnFilters();
                          }}
                        />
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleReset}
                      sx={{ textTransform: "none" }}
                    >
                      Reset All
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: showAllFilters ? 540 : 450,
                  overflowY: filteredData.length > rowsPerPage ? "auto" : "visible",
                  overflowX: "auto",
                  scrollbarGutter: "stable",
                }}
              >
                <Table stickyHeader>
                  <TableHead sx={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "employeeId"}
                          direction={valueToOrderBy === "employeeId" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("employeeId")}
                        >
                          Employee ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "employeeName"}
                          direction={valueToOrderBy === "employeeName" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("employeeName")}
                        >
                          Employee Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "department"}
                          direction={valueToOrderBy === "department" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("department")}
                        >
                          Department
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "designation"}
                          direction={valueToOrderBy === "designation" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("designation")}
                        >
                          Designation
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "role"}
                          direction={valueToOrderBy === "role" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("role")}
                        >
                          Role
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "doj"}
                          direction={valueToOrderBy === "doj" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("doj")}
                        >
                          DOJ
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "reportingUnder"}
                          direction={valueToOrderBy === "reportingUnder" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("reportingUnder")}
                        >
                          Reporting Under
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "deputyManager"}
                          direction={valueToOrderBy === "deputyManager" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("deputyManager")}
                        >
                          Deputy Manager
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={valueToOrderBy === "createdDate"}
                          direction={valueToOrderBy === "createdDate" ? orderDirection : "desc"}
                          onClick={() => handlRequestSort("createdDate")}
                        >
                          Created Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                    {showAllFilters && (
                      <TableRow>
                        {[
                          "employeeId",
                          "employeeName",
                          "department",
                          "designation",
                          "role",
                          "doj",
                          "reportingUnder",
                          "deputyManager",
                          "createdDate",
                        ].map((field) => {
                          const isDateField = field === "doj" || field === "createdDate";

                          return (
                            <TableCell key={field}>
                              <TextField
                                type={isDateField ? "date" : "text"}
                                size="small"
                                fullWidth
                                placeholder={isDateField ? "" : "Search..."}
                                value={searchText[field] || ""}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  setSearchText((prev) => {
                                    const updated = { ...prev };

                                    // For date fields, store the value as is (yyyy-MM-dd)
                                    // For text fields, store the trimmed value
                                    if (value?.trim()) {
                                      updated[field] = value;
                                    } else {
                                      delete updated[field];
                                    }

                                    return updated;
                                  });

                                  setGlobalSearch("");
                                  setPage(0);
                                }}
                                InputLabelProps={
                                  isDateField
                                    ? {
                                      shrink: true,
                                    }
                                    : undefined
                                }
                                InputProps={{
                                  endAdornment: isColumnFiltered(field) && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => clearColumnFilter(field)}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </TableCell>
                          );
                        })}
                        <TableCell />
                        <TableCell />
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {loading && initialLoad ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                          {hasColumnFilters || globalSearch ? (
                            <>
                              <Typography color="text.secondary">No records match the applied filters</Typography>
                              <Button
                                size="small"
                                sx={{ mt: 1 }}
                                onClick={() => {
                                  resetColumnFilters();
                                  handleReset();
                                }}
                              >
                                Clear Filters
                              </Button>
                            </>
                          ) : (
                            <Typography color="text.secondary">No pending approvals found.</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((row) => {
                        return (
                          <TableRow key={row?.personId || row?.employeeId || row?.id} hover>
                            <TableCell>{row?.employeeId || row?.personId || row?.employeeID || "-"}</TableCell>
                            <TableCell>{row?.employeeName || [row?.firstName, row?.middleName, row?.lastName].filter(Boolean).join(" ") || "-"}</TableCell>
                            <TableCell>{row?.department || row?.deptName || "-"}</TableCell>
                            <TableCell>{row?.designation || row?.empPosition || "-"}</TableCell>
                            <TableCell>{row?.role || "-"}</TableCell>
                            <TableCell>
                              {formatDate(row?.doj || row?.dateOfJoining)}
                            </TableCell>
                            <TableCell>{row?.reportingUnder || row?.reportingTo || "-"}</TableCell>
                            <TableCell>{row?.deputyManager || row?.managerName || "-"}</TableCell>
                            <TableCell>
                              {formatDate(row?.createdDate || row?.createdOn)}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                <Tooltip title="View" placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewEmployee(row?.employeeId || row?.personId)}
                                  >
                                    <RemoveRedEyeOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => {
                                    setSelectedRecord(row);
                                    handleApprove();
                                  }}
                                  disabled={submitting}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "65px",
                                    fontSize: "0.75rem",
                                    py: 0.5
                                  }}
                                >
                                  {submitting ? "..." : "Approve"}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="error"
                                  onClick={() => {
                                    setSelectedRecord(row);
                                    setRejectOpen(true);
                                  }}
                                  disabled={submitting}
                                  sx={{
                                    textTransform: "none",
                                    minWidth: "65px",
                                    fontSize: "0.75rem",
                                    py: 0.5
                                  }}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  colSpan={11}
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </Box>
            </Box>
          </Card>
        </Card>
      </PageContainer>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>Employee Details</DialogTitle>
        <DialogContent dividers>
          {renderDetails(selectedRecord)}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setDetailsOpen(false);
              // Navigate to view page instead of opening approve dialog
              if (selectedRecord) {
                handleViewEmployee(selectedRecord?.employeeId || selectedRecord?.personId);
              }
            }}
            startIcon={<RemoveRedEyeOutlinedIcon />}
          >
            View Full Details
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setDetailsOpen(false);
              setApproveOpen(true);
            }}
            startIcon={<CheckCircleOutlineOutlinedIcon />}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setDetailsOpen(false);
              setRejectOpen(true);
            }}
            startIcon={<HighlightOffOutlinedIcon />}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to approve this employee?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={submitting}>
            {submitting ? "Processing..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogTitle>Reject Employee</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={submitting}>
            {submitting ? "Processing..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmployeeApprovalPage;